import { useMemo } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { getOfferingsBatch } from 'services/offerings';
import { getElasticOfferingSearch } from 'services/elastic-search';
import { getPageData } from 'utils/lib/strapi';

/**
 * @typedef {Object} UseGetPrivateOfferingsOptions
 * @property {boolean} [approvedOnly]
 * @property {string|string[]} [partners]
 * @property {boolean} [enabled]
 */

/**
 * @param {UseGetPrivateOfferingsOptions} [options]
 */
const useGetPrivateOfferings = ({ approvedOnly = false, partners, enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const shouldFetch = enabled !== false;

  const strapiContentId = queryClient.getQueryData(['strapiContentId']);

  const {
    data: strapiData,
    isSuccess: strapiSuccess,
    isLoading: isLoadingStrapi,
  } = useQuery({
    queryKey: ['privateLandingPagePinnedExcludedTombstones', approvedOnly, strapiContentId],
    queryFn: () =>
      getPageData({
        type: 'private-landing-pages',
        preview: Boolean(strapiContentId),
        id: strapiContentId,
      }),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: elasticOfferings,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isPending: isPendingElastic,
    isSuccess: isElasticSuccess,
  } = useInfiniteQuery({
    queryKey: ['privateLandingPageTombstones', approvedOnly, partners],
    queryFn: async ({ pageParam = 0 }) => {
      try {
        return await getElasticOfferingSearch({
          query: '',
          statuses: approvedOnly ? 'APPROVED' : 'APPROVED,TESTING,CLOSED',
          page: pageParam,
          size: 200,
          regulationTypes: ['Reg D'],
          partners,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching elastic offerings:', error);
        return [];
      }
    },
    getNextPageParam: (lastPage) => {
      const { pageNumber, totalPages } = lastPage || {};
      return pageNumber < (totalPages || 0) - 1 ? pageNumber + 1 : undefined;
    },
    enabled: shouldFetch,
  });

  const strapiExcludeOfferings = useMemo(
    () =>
      strapiSuccess && strapiData?.attributes?.offeringSlugsExclude
        ? strapiData.attributes.offeringSlugsExclude.map((slug) => slug?.value)
        : [],
    [strapiSuccess, strapiData?.attributes?.offeringSlugsExclude],
  );

  const strapiPriorityOfferings = useMemo(
    () =>
      strapiSuccess && strapiData?.attributes?.offeringSlugsPin
        ? strapiData.attributes.offeringSlugsPin.map((slug) => slug?.value)
        : [],
    [strapiSuccess, strapiData?.attributes?.offeringSlugsPin],
  );

  const elasticSlugSet = useMemo(() => {
    const set = new Set();
    for (const page of elasticOfferings?.pages || []) {
      for (const offering of page?.list || []) {
        if (offering?.slug) set.add(offering.slug);
      }
    }
    return set;
  }, [elasticOfferings?.pages]);

  // Gate on isElasticSuccess so we only direct-fetch slugs elastic genuinely doesn't
  // return — without this gate, an early run with empty elasticSlugSet would batch every
  // pinned slug and re-introduce the race condition this hook is designed to avoid.
  const missingPinnedSlugs = useMemo(() => {
    if (!shouldFetch || !isElasticSuccess) return [];
    return strapiPriorityOfferings.filter((slug) => slug && !elasticSlugSet.has(slug));
  }, [shouldFetch, isElasticSuccess, strapiPriorityOfferings, elasticSlugSet]);

  // Elastic's lightweight Reg D search rows deliberately omit the offering
  // ceiling (`maximumFundingAmount`).  The editorial private tombstones need
  // that ceiling to show a truthful percent-left rail, so hydrate only the
  // open rows from the canonical offering endpoint. Closed rows do not render
  // an availability rail and do not need the additional request.
  const openElasticSlugs = useMemo(() => {
    if (!shouldFetch || !isElasticSuccess) return [];

    const slugs = new Set();
    for (const page of elasticOfferings?.pages || []) {
      for (const offering of page?.list || []) {
        const isOpen = offering?.status === 'approved' || offering?.status === 'testing';
        if (isOpen && offering?.slug && !strapiExcludeOfferings.includes(offering.slug)) {
          slugs.add(offering.slug);
        }
      }
    }
    return Array.from(slugs).sort();
  }, [shouldFetch, isElasticSuccess, elasticOfferings?.pages, strapiExcludeOfferings]);

  const { data: openOfferingDetails, isPending: isPendingOpenOfferingDetails } = useQuery({
    queryKey: ['privateLandingPageOpenOfferingDetails', openElasticSlugs, approvedOnly, partners],
    queryFn: async () => {
      if (!openElasticSlugs.length) return new Map();
      return getOfferingsBatch(openElasticSlugs);
    },
    enabled: shouldFetch && isElasticSuccess && openElasticSlugs.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const { data: missingPinnedOfferings, isPending: isPendingMissingPinned } = useQuery({
    queryKey: ['privateLandingPageMissingPinnedOfferings', missingPinnedSlugs, approvedOnly],
    queryFn: async () => {
      if (!missingPinnedSlugs.length) return [];
      const batchMap = await getOfferingsBatch(missingPinnedSlugs);
      return Array.from(batchMap.values());
    },
    enabled: shouldFetch && isElasticSuccess && missingPinnedSlugs.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const privateOfferings = useMemo(() => {
    if (!shouldFetch) return [];

    const allMap = new Map();
    for (const o of missingPinnedOfferings || []) {
      if (o?.slug) allMap.set(o.slug, o);
    }
    for (const page of elasticOfferings?.pages || []) {
      for (const o of page?.list || []) {
        if (o && !strapiExcludeOfferings.includes(o.slug) && !allMap.has(o.slug)) {
          // Keep the search row's identity/status but fill its omitted
          // financial fields from the canonical detail response.
          const detail = openOfferingDetails?.get?.(o.slug);
          allMap.set(o.slug, detail ? { ...o, ...detail, slug: o.slug } : o);
        }
      }
    }

    const pinned = strapiPriorityOfferings.map((slug) => allMap.get(slug)).filter(Boolean);

    const pinSet = new Set(strapiPriorityOfferings);
    const remaining = Array.from(allMap.values()).filter((o) => !pinSet.has(o.slug));

    const combined = [...pinned, ...remaining];

    const filteredOfferings = approvedOnly
      ? combined.filter((offering) => offering?.status === 'approved')
      : combined;

    // Both "approved" and "testing" are actively accepting interest, so group them
    // together at the top — closed/other statuses fall below.
    const isOpenStatus = (status) => status === 'approved' || status === 'testing';
    return filteredOfferings.sort(
      (a, b) => (isOpenStatus(b?.status) ? 1 : 0) - (isOpenStatus(a?.status) ? 1 : 0),
    );
  }, [
    shouldFetch,
    missingPinnedOfferings,
    approvedOnly,
    elasticOfferings?.pages,
    openOfferingDetails,
    strapiExcludeOfferings,
    strapiPriorityOfferings,
  ]);

  const showSeriesSpotlight = shouldFetch
    ? (strapiData?.attributes?.showSeriesSpotlight ?? false)
    : false;

  const seriesSpotlightSlug = strapiData?.attributes?.seriesSpotlightSlug || 'catalyst-fund-ira';

  const totalElements = shouldFetch ? elasticOfferings?.pages?.[0]?.totalElements : undefined;

  // isPending = no cached data + initial load only (does not flip on background refetches),
  // so consumers like ExploreV2 won't flicker offerings on window-focus invalidation.
  const isLoadingOfferings =
    shouldFetch &&
    (isLoadingStrapi ||
      isPendingElastic ||
      (isElasticSuccess && missingPinnedSlugs.length > 0 && isPendingMissingPinned) ||
      (isElasticSuccess && openElasticSlugs.length > 0 && isPendingOpenOfferingDetails));

  return {
    data: privateOfferings,
    totalElements,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    offeringData: missingPinnedOfferings,
    isLoadingOfferings,
    showSeriesSpotlight,
    seriesSpotlightSlug,
    strapiSuccess,
  };
};

export default useGetPrivateOfferings;
